import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImageUp, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { StatusLine } from "@/components/CategoryGrid";

const imgSrc = (url) => (url && url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

function PhotoCard({ cat, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/categories/${cat.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${cat.name}: photo updated (${data.width} × ${data.height})`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-[#E8ECE9] shadow-sm" data-testid={`category-photo-card-${cat.slug}`}>
      <div className="aspect-[16/9] bg-[#E8ECE9] overflow-hidden">
        {cat.image_url && <img src={imgSrc(cat.image_url)} alt={cat.name} className="w-full h-full object-cover" data-testid={`category-photo-img-${cat.slug}`} />}
      </div>
      <div className="p-5">
        <h3 className="font-serif text-lg text-[#11211A] leading-snug">{cat.name}</h3>
        <StatusLine cat={cat} className="mt-1 !text-xs" />
        <div className="mt-4">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50" data-testid={`category-photo-change-${cat.slug}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageUp className="w-3.5 h-3.5" />} {busy ? "Uploading…" : "Change photo"}
          </button>
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`category-photo-input-${cat.slug}`} />
        </div>
        <p className="text-[11px] text-[#7D8C85] mt-3 leading-relaxed" data-testid={`category-photo-note-${cat.slug}`}>
          Recommended 1600 × 900 px (16:9), JPG or PNG. Minimum 1200 × 675 px — smaller files are rejected. Shows on the homepage and Categories page immediately.
          {cat.image_updated_at && <span className="block mt-0.5">Last changed {new Date(cat.image_updated_at).toLocaleDateString()}.</span>}
        </p>
      </div>
    </div>
  );
}

export default function CategoryPhotosManager() {
  const [cats, setCats] = useState(null);
  const load = () => api.get("/categories/tree").then(({ data }) => setCats(data)).catch(() => setCats([]));
  useEffect(() => { load(); }, []);

  return (
    <section className="bg-white border border-[#E8ECE9] p-6" data-testid="category-photos-manager">
      <div className="tracking-overline mb-1">Homepage &amp; Categories page</div>
      <h2 className="font-serif text-2xl text-[#11211A]">Category photos</h2>
      <p className="text-sm text-[#7D8C85] mt-1 mb-6">The eight category cards and their current photos. A new upload replaces the old picture everywhere straight away — no redeploy, and no old cached copy, even if the file name is the same.</p>
      {!cats ? <div className="text-sm text-[#7D8C85]">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="category-photos-grid">
          {cats.map((c) => <PhotoCard key={c.id} cat={c} onChanged={load} />)}
        </div>
      )}
    </section>
  );
}
